import { EFinancingCompany } from '@if/core/types'
import { omit } from 'lodash-es'
import { IcebergUser, UserRight, UserRights } from '../types/IcebergUser'

export const JWT_LOCAL_STORAGE_KEY = 'jwtToken'
const IMPERSONATED_SESSION_LOCAL_STORAGE_KEY = 'impersonatedSession'

export function parseJwt(token: string): object {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => {
        return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`
      })
      .join(''),
  )

  return JSON.parse(jsonPayload) as object
}

function buildRights(jwtRights: UserRight[]): UserRights {
  const ret = {} as UserRights
  jwtRights.forEach((r: UserRight) => {
    ret[r] = true
  })
  return ret
}

export function saveUserToken(jwtToken: string): void {
  localStorage.setItem(JWT_LOCAL_STORAGE_KEY, jwtToken)
}

export function getUserToken(): string {
  const token = localStorage.getItem(JWT_LOCAL_STORAGE_KEY)
  return token || ''
}

export function markImpersonatedSession(): void {
  localStorage.setItem(IMPERSONATED_SESSION_LOCAL_STORAGE_KEY, 'true')
}

export function clearImpersonatedSession(): void {
  localStorage.removeItem(IMPERSONATED_SESSION_LOCAL_STORAGE_KEY)
}

/**
 * An impersonated session must not be silently replaced by the Azure AD account that may still be
 * cached in MSAL: without this marker the sign-in effect would take over as soon as the
 * impersonated token expires or a call returns 401. Ended by logging out, which wipes local
 * storage, or by signing in through Azure AD again.
 */
export function isImpersonatedSession(): boolean {
  return localStorage.getItem(IMPERSONATED_SESSION_LOCAL_STORAGE_KEY) === 'true'
}

export function loadUserFromJwtString(jwtToken: string): IcebergUser {
  const dto = parseJwt(jwtToken) as UserDto
  dto.companyIds ??= ''
  const user: IcebergUser = {
    ...omit(dto, ['roles', 'rights', 'buyDownRebateMax']),
    isInternal: dto.isInternal === 'true',
    roles: dto.roles.split(','),
    rights: buildRights(dto.rights.split(',') as UserRight[]),
    companyIds: dto.companyIds.split(',') as EFinancingCompany[],
    buyDownRebateMax: dto.buyDownRebateMax ? Number(dto.buyDownRebateMax) : 0,
  }
  return user
}

export function isExpiredToken(expiryDate: number): boolean {
  const date = new Date().valueOf() / 1000
  return date > expiryDate
}

export interface UserDto {
  id: string
  username: string
  name: string
  roles: string
  rights: string
  langId: string
  isInternal: string
  companyIds: string
  buyDownRebateMax: string
  exp: number /* expire date in ms. to get the date, do new Date(user.exp * 1000) */
}
